import { useEffect, useState } from 'react';
import type { OwnerProfile } from '@kairelo/shared';
import { profile as profileApi, ApiRequestError } from '../lib/api.js';
import { Icon } from '../components/Icon.tsx';
import { Button, Card, Field, Loading, Notice, useToast } from '../components/ui.tsx';
import { ProfileImport } from '../components/ProfileImport.tsx';

/**
 * Owner profile (concept §2).
 *
 * Profile content is owner-published and deliberately NOT end-to-end encrypted:
 * a visitor must view it before any conversation key exists (§6). The page says
 * so once, quietly, rather than leading with a banner.
 */
export function ProfileEditor() {
  const toast = useToast();
  const [profile, setProfile] = useState<OwnerProfile | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [headline, setHeadline] = useState('');
  const [bio, setBio] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    profileApi
      .get()
      .then((loaded) => {
        setProfile(loaded);
        setDisplayName(loaded.displayName);
        setHeadline(loaded.headline ?? '');
        setBio(loaded.bio ?? '');
      })
      .catch(() => setError('Your profile could not be loaded.'));
  }, []);

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      const updated = await profileApi.update({
        displayName: displayName.trim(),
        headline: headline.trim() || null,
        bio: bio.trim() || null,
      });
      setProfile(updated);
      toast('success', 'Profile saved.');
    } catch (saveError) {
      setError(saveError instanceof ApiRequestError ? saveError.message : 'Saving failed.');
    } finally {
      setSaving(false);
    }
  };

  const upload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const ticket = await profileApi.requestMediaUpload(file.type, file.size);
      const response = await fetch(ticket.uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': ticket.requiredContentType },
        body: file,
      });
      if (!response.ok) throw new Error('The upload failed.');
      setProfile(await profileApi.get());
      toast('success', 'Media added.');
    } catch (uploadError) {
      setError(
        uploadError instanceof ApiRequestError
          ? uploadError.message
          : 'That file could not be uploaded.',
      );
    } finally {
      setUploading(false);
    }
  };

  if (!profile && !error) {
    return (
      <div className="page">
        <Loading>Loading your profile…</Loading>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page__header">
        <span className="tile tile--accent" aria-hidden="true">
          <Icon name="user" size={20} />
        </span>
        <div className="page__title">
          <h1>Profile</h1>
          <p>What a visitor sees before they decide to join.</p>
        </div>
      </header>

      {error ? <Notice tone="danger">{error}</Notice> : null}

      {profile?.features.profileImport ? (
        <ProfileImport
          theme={profile.theme}
          onApply={(draft) => {
            // Fills the form only. Saving stays an explicit act, so a draft the
            // owner dislikes is discarded by navigating away.
            if (draft.displayName) setDisplayName(draft.displayName);
            if (draft.headline) setHeadline(draft.headline);
            if (draft.bio) setBio(draft.bio);
            toast('info', 'Draft applied. Review it, then press Save profile.');
          }}
          onMediaChanged={() => {
            void profileApi.get().then(setProfile);
          }}
        />
      ) : null}

      <Card
        title="About you"
        description="Shown on every invitation link you send."
        footer={
          <>
            <span className="spacer" />
            <Button variant="primary" icon="check" busy={saving} onClick={() => void save()}>
              Save profile
            </Button>
          </>
        }
      >
        <Field label="Display name" htmlFor="display-name">
          {({ id }) => (
            <input
              id={id}
              type="text"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              maxLength={120}
            />
          )}
        </Field>

        <Field label="Headline" htmlFor="headline" hint="One line about what you do.">
          {({ id, describedBy }) => (
            <input
              id={id}
              type="text"
              value={headline}
              aria-describedby={describedBy}
              onChange={(event) => setHeadline(event.target.value)}
              maxLength={200}
              placeholder="Consultations by appointment"
            />
          )}
        </Field>

        <Field label="About" htmlFor="bio">
          {({ id }) => (
            <textarea
              id={id}
              value={bio}
              onChange={(event) => setBio(event.target.value)}
              maxLength={4000}
              placeholder="A short introduction. This is public to anyone holding one of your links."
            />
          )}
        </Field>
      </Card>

      <Card title="Photos and videos" description="JPEG, PNG, WebP, GIF, MP4 or WebM.">
        {profile && profile.media.length > 0 ? (
          <div className="media-strip" style={{ marginBottom: 'var(--s-4)' }}>
            {profile.media.map((item) => (
              <div className="media-tile" key={item.objectKey}>
                {item.kind === 'photo' ? (
                  <img src={item.url} alt="" />
                ) : (
                  <video src={item.url} controls playsInline />
                )}
                <Button
                  size="sm"
                  iconOnly
                  icon="trash"
                  variant="danger"
                  onClick={() =>
                    void profileApi
                      .deleteMedia(item.id)
                      .then(() => profileApi.get())
                      .then((next) => {
                        setProfile(next);
                        toast('success', `Removed ${item.kind}.`);
                      })
                  }
                >
                  {`Remove ${item.kind}`}
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm muted" style={{ marginBottom: 'var(--s-4)' }}>
            No profile media yet.
          </p>
        )}

        <label className="filedrop" htmlFor="profile-media">
          <Icon name="image" size={20} />
          <span>
            {uploading ? (
              <>
                <span className="spinner" aria-hidden="true" /> Uploading…
              </>
            ) : (
              <>
                <strong>Add a photo or video</strong>
                <br />
                Click to choose a file from this device.
              </>
            )}
          </span>
          <input
            id="profile-media"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm"
            disabled={uploading}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void upload(file);
              event.target.value = '';
            }}
          />
        </label>
      </Card>

      <Notice title="Your profile is public to anyone holding one of your links">
        Profile text, photos and videos are stored with ordinary access controls so visitors can see
        them before a conversation starts. They are <em>not</em> end-to-end encrypted, and are never
        described as such. Conversation content is a separate matter and is encrypted in the
        browser.
      </Notice>
    </div>
  );
}
